import React, {useEffect, useMemo, useCallback} from 'react'
import styled from 'styled-components'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Button from '@mui/material/Button'
import pdfIcon from '../../../assets/pdf.png'
import docxIcon from '../../../assets/docx.png'
import corret from '../../../assets/correctAnalysis.svg'
import aiIcon from '../../../assets/aiIcon.svg'
import warning from '../../../assets/warningAnalysis.svg'
import error from '../../../assets/errorAnalysis.svg'
import empty from '../../../assets/projeto-vazio.svg'
import {
    Alert,
    Box,
    CircularProgress,
    LinearProgress,
    Modal,
    Pagination,
    Skeleton,
    Tooltip,
    FormControl,
    Select,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material'
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {AnalyzerApiService} from '../../../services/AnalyzerApi.service'
import {DocAnalysisResponse} from 'types/docAnalyses'
import {AnalyzedItemResponse} from 'types/analyzedItens'
import {HistoryContext} from '../../../contexts/history'
import Snackbar, {
    SnackbarCloseReason,
    SnackbarOrigin,
} from '@mui/material/Snackbar'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import InfoIcon from '@mui/icons-material/Info'
import {getCurrentColors} from '../../../utils/colors'
import {generateAnalysisPdf} from '../../../components/AnalysisPdf'

const colors = getCurrentColors()
const MAX_FILE_SIZE_MB = 20
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

interface DocType {
    id: string
    apiId: string
    short: string
    name: string
    desc: string
    accent: string
    soft: string
    iconPath: string
    phase: string
    avgPages: string
    featured?: boolean
}

const DOC_TYPES: DocType[] = [
    {
        id: 'edital',
        apiId: '3514c57a-6ed3-4472-b4d5-2f1d9815400f',
        short: 'EDITAL',
        name: 'Edital (com ou sem anexos)',
        desc: 'Recomendado: envie o edital consolidado com seus anexos (TR, minuta de contrato, planilhas) em um único arquivo.',
        accent: '#050B29',
        soft: 'rgba(5,11,41,0.07)',
        iconPath: 'M3 4h18v4H3zM5 8v12h14V8M9 13h6M9 17h6',
        phase: 'Seleção',
        avgPages: '40-150 págs',
        featured: true,
    },
    {
        id: 'tr',
        apiId: '8b34584f-7781-4c91-8bd2-2f79c3889a6e',
        short: 'TR',
        name: 'Termo de Referência',
        desc: 'Especificação técnica detalhada do objeto da contratação.',
        accent: '#3acdb4',
        soft: 'rgba(58,205,180,0.12)',
        iconPath: 'M4 4h16v4H4zM4 10h10v10H4zM16 10h4v4h-4zM16 16h4v4h-4z',
        phase: 'Planejamento',
        avgPages: '20-60 págs',
    },
    {
        id: 'etp',
        apiId: 'c8eb8b29-758e-4898-9f10-e10c7d35e10f',
        short: 'ETP',
        name: 'Estudo Técnico Preliminar',
        desc: 'Documento que materializa o estudo técnico que fundamenta a contratação.',
        accent: '#3394EF',
        soft: 'rgba(51,148,239,0.10)',
        iconPath:
            'M9 3h6l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M14 3v5h5M8 13h8M8 17h5',
        phase: 'Planejamento',
        avgPages: '12-30 págs',
    },
    {
        id: 'contrato',
        apiId: 'b7799217-ba68-408f-931d-ab3b1f28b9aa',
        short: 'CONTRATO',
        name: 'Contrato Administrativo',
        desc: 'Instrumento que disciplina as obrigações entre Administração e contratado.',
        accent: '#B31417',
        soft: 'rgba(179,20,23,0.08)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13l-4 4-2-2',
        phase: 'Execução',
        avgPages: '15-50 págs',
    },
    {
        id: 'mr',
        apiId: '',
        short: 'MR',
        name: 'Mapa de Riscos',
        desc: 'Identificação, avaliação e tratamento de riscos da contratação.',
        accent: '#E0951B',
        soft: 'rgba(224,149,27,0.12)',
        iconPath: 'M12 3 2 21h20L12 3zM12 10v5M12 17.5h0.01',
        phase: 'Planejamento',
        avgPages: '5-15 págs',
    },
    {
        id: 'dfd',
        apiId: '3b86581f-cf66-40d1-96b3-950a9d709827',
        short: 'DFD',
        name: 'Documento de Formalização da Demanda',
        desc: 'Marco inicial do planejamento — registra a necessidade da contratação.',
        accent: '#7C5CFF',
        soft: 'rgba(124,92,255,0.10)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h4',
        phase: 'Planejamento',
        avgPages: '2-6 págs',
    },
    {
        id: 'ata',
        apiId: 'b7287010-9bfe-419f-bb0c-b768cb1ac739',
        short: 'ATA',
        name: 'Ata de Registro de Preços',
        desc: 'Documento vinculativo, obrigacional, com características de compromisso para futura contratação.',
        accent: '#1F8D7A',
        soft: 'rgba(31,141,122,0.10)',
        iconPath:
            'M8 2h8a2 2 0 0 1 2 2v18l-6-3-6 3V4a2 2 0 0 1 2-2zM10 8h4M10 12h4',
        phase: 'Contratação',
        avgPages: '8-25 págs',
    },
]

interface State extends SnackbarOrigin {
    open: boolean
}

const Section = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
    background-color: #f6f8fa;
    padding: 0;
    overflow-y: auto;
`
const Body = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    margin: 10px 40px 40px 40px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 90%;
`

const Title = styled.span`
    margin-top: 40px;
    font-size: 24px;
    text-align: start;
    top: 58px;
`

export default function DocumentAnalyzer() {
    const [file, setFile] = React.useState<File | null>(null)
    const [isLoading, setIsLoading] = React.useState(false)
    const [progress, setProgress] = React.useState(0)
    const [loadingMessage, setLoadingMessage] =
        React.useState('Lendo os textos')
    const analyzerApi = useMemo(() => new AnalyzerApiService(), [])
    const api = useMemo(() => new AnalyzerApiService(), [])
    const [DocAnalysisList, setDocAnalysisList] =
        React.useState<DocAnalysisResponse>([])
    const [docIdsList, setDocIdsList] = React.useState<string[]>([])
    const [analyzedItems, setAnalyzedItems] = React.useState<
        AnalyzedItemResponse[]
    >([])
    const [detailsLoading, setDetailsLoading] = React.useState<
        Record<number, boolean>
    >({})
    const [expandedIndex, setExpandedIndex] = React.useState<number | null>(0)
    const [uploadPhase, setUploadPhase] = React.useState<
        'idle' | 'uploading' | 'analyzing'
    >('idle')

    const {redSocketMessage, setRedSocketMessage, getProjects} =
        React.useContext(HistoryContext)

    const [loadingList, setLoadingList] = React.useState(true)
    const [selectedDocType, setSelectedDocType] = React.useState<string>('')

    const visibleDocumentTypes = React.useMemo<DocType[]>(() => {
        if (!docIdsList.length) return DOC_TYPES
        const availableIds = new Set(docIdsList)
        return DOC_TYPES.filter(
            (doc) => doc.apiId && availableIds.has(doc.apiId)
        )
    }, [docIdsList])

    const selectedDocumentType = React.useMemo(() => {
        return (
            visibleDocumentTypes.find(
                (item) => item.apiId === selectedDocType
            ) ||
            visibleDocumentTypes[0] ||
            null
        )
    }, [selectedDocType, visibleDocumentTypes])

    const selectedDocBg = React.useMemo(() => {
        if (selectedDocumentType) return selectedDocumentType.soft
        return '#f5f5f5'
    }, [selectedDocumentType])

    React.useEffect(() => {
        if (visibleDocumentTypes.length === 0) return

        const hasSelectedType = visibleDocumentTypes.some(
            (item) => item.apiId === selectedDocType
        )

        if (!hasSelectedType) {
            setSelectedDocType(visibleDocumentTypes[0].apiId)
        }
    }, [visibleDocumentTypes, selectedDocType])

    const [page, setPage] = React.useState(1)
    const [totalPages, setTotalPages] = React.useState(1)
    const [Toast, setToast] = React.useState<State>({
        open: true,
        vertical: 'top',
        horizontal: 'center',
    })
    const [pdfAlertMessage, setPdfAlertMessage] = React.useState('')
    const [modal, setModal] = React.useState(false)
    const [documentToDelete, setDocumentToDelete] = React.useState<
        string | null
    >(null)
    const [showAlertFileSize, setShowAlertFileSize] = React.useState(false)

    const validateFileSize = useCallback((selectedFile: File) => {
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setShowAlertFileSize(true)
            return false
        }

        setShowAlertFileSize(false)

        return true
    }, [])

    const handleCloseToast = (
        _event: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason
    ) => {
        if (reason === 'clickaway') {
            return
        }

        setToast((prev) => ({...prev, open: false}))
    }

    const resetRedSocketMessage = useCallback(() => {
        setRedSocketMessage({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    }, [setRedSocketMessage])

    const loadAnalyses = useCallback(async () => {
        setLoadingList(true)
        console.log('Carregando análises de documentos, página:', page)
        const res = await api.listDocumentAnalyses(4, (page - 1) * 4)
        setLoadingList(false)
        setTotalPages(Math.ceil(res.total / 4))
        return res.documents
    }, [api, page])

    const refreshAnalyses = useCallback(async () => {
        try {
            const res = await loadAnalyses()
            setAnalyzedItems([])
            setDocAnalysisList(res)
            setExpandedIndex(0)
        } catch (error) {
            console.error('Erro ao carregar análises de documentos:', error)
        }
    }, [loadAnalyses])

    const fetchAnalyzedItems = useCallback(
        async (
            docAnalysisUuid: string,
            index: number
        ): Promise<AnalyzedItemResponse> => {
            setDetailsLoading((prev) => ({...prev, [index]: true}))
            try {
                const analyzedResponse =
                    await api.listAnalyzedItems(docAnalysisUuid)
                const items = analyzedResponse.items || []
                setAnalyzedItems((prev) => {
                    const next = [...prev]
                    next[index] = items
                    return next
                })
                return items
            } catch (error) {
                console.error('Erro ao carregar detalhes do documento:', error)
                setAnalyzedItems((prev) => {
                    const next = [...prev]
                    next[index] = []
                    return next
                })
                return []
            } finally {
                setDetailsLoading((prev) => ({...prev, [index]: false}))
            }
        },
        [api]
    )

    const handleAccordionChange = useCallback(
        (index: number, _docAnalysisUuid: string) =>
            (_event: React.SyntheticEvent, isExpanded: boolean) => {
                setExpandedIndex(isExpanded ? index : null)
            },
        []
    )

    const handleDownloadAnalysisPDF = useCallback(
        async (
            event: React.MouseEvent<HTMLButtonElement>,
            index: number,
            docAnalysisUuid: string,
            documentName: string
        ) => {
            event.stopPropagation()
            setPdfAlertMessage('')

            if (detailsLoading[index]) {
                return
            }

            let itemsToDownload = analyzedItems[index]

            if (!itemsToDownload) {
                itemsToDownload = await fetchAnalyzedItems(
                    docAnalysisUuid,
                    index
                )
            }

            if (!itemsToDownload || itemsToDownload.length === 0) {
                setPdfAlertMessage(
                    'A análise ainda não possui itens carregados para exportação em PDF.'
                )
                return
            }

            await generateAnalysisPdf(itemsToDownload, documentName)
        },
        [analyzedItems, detailsLoading, fetchAnalyzedItems]
    )

    const getFileExtension = useCallback((fileNameOrUrl?: string | null) => {
        if (!fileNameOrUrl) return ''

        const sanitizedValue = fileNameOrUrl.split('?')[0].toLowerCase()
        const extension = sanitizedValue.split('.').pop()

        return extension ?? ''
    }, [])

    const getFileIcon = useCallback(
        (fileNameOrUrl?: string | null) => {
            const extension = getFileExtension(fileNameOrUrl)

            if (extension === 'docx') {
                return docxIcon
            }

            return pdfIcon
        },
        [getFileExtension]
    )

    const getStatusVisuals = useCallback((status: number) => {
        switch (status) {
            case 0:
                return {
                    icon: corret,
                    label: 'Pontos Positivos',
                    color: '#2e7d32',
                }
            case 1:
                return {
                    icon: warning,
                    label: 'Pontos de Alerta',
                    color: '#ed6c02',
                }
            case 2:
                return {icon: error, label: 'Pontos de erro', color: '#d32f2f'}
            default:
                return {icon: aiIcon, label: 'Info', color: '#1976d2'}
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const fetchDocumentTypeIds = async () => {
            try {
                const res = await api.documentsTypesIds()
                if (isMounted) {
                    setDocIdsList(res)
                }
            } catch (error) {
                console.error('Erro ao carregar tipos de documento:', error)
            }
        }

        fetchDocumentTypeIds()

        return () => {
            isMounted = false
        }
    }, [api])

    useEffect(() => {
        let isMounted = true

        const fetchAnalyses = async () => {
            try {
                const res = await loadAnalyses()
                if (isMounted) {
                    setDocAnalysisList(res)
                }
            } catch (error) {
                console.error('Erro ao carregar análises de documentos:', error)
            }
        }

        fetchAnalyses()

        return () => {
            isMounted = false
        }
    }, [loadAnalyses])

    React.useEffect(() => {
        let timer: NodeJS.Timeout
        if (isLoading) {
            timer = setInterval(() => {
                setProgress((prevProgress) =>
                    prevProgress >= 100 ? 100 : prevProgress + 10
                )
            }, 500)
        }
        return () => {
            clearInterval(timer)
        }
    }, [isLoading])

    useEffect(() => {
        if (
            expandedIndex !== null &&
            DocAnalysisList[expandedIndex] &&
            !analyzedItems[expandedIndex]
        ) {
            fetchAnalyzedItems(
                DocAnalysisList[expandedIndex].doc_analysis_uuid,
                expandedIndex
            )
        }
    }, [DocAnalysisList, expandedIndex, analyzedItems, fetchAnalyzedItems])

    useEffect(() => {
        if (!redSocketMessage?.status) {
            return
        }

        console.log('Red Socket Message received:', redSocketMessage)

        const normalizedStatus = redSocketMessage.status.toUpperCase()

        if (normalizedStatus === 'UPLOADED' && uploadPhase !== 'idle') {
            setUploadPhase('analyzing')
            setLoadingMessage('Arquivo enviado. Analisando documento...')
            resetRedSocketMessage()
            return
        }

        if (normalizedStatus === 'FINISHED') {
            if (uploadPhase !== 'idle') {
                setLoadingMessage('Análise concluída. Atualizando lista...')
                setProgress(100)
                setIsLoading(false)
                setUploadPhase('idle')
                setFile(null)
                setTimeout(() => setProgress(0), 300)
            }

            refreshAnalyses()
            getProjects()
            resetRedSocketMessage()
        }
    }, [
        redSocketMessage,
        uploadPhase,
        resetRedSocketMessage,
        refreshAnalyses,
        getProjects,
    ])

    const handleAnalyzeProject = useCallback(
        async (selectedFile: File) => {
            if (!validateFileSize(selectedFile)) {
                return
            }

            setFile(selectedFile)
            setIsLoading(true)
            setProgress(0)
            setUploadPhase('uploading')
            setLoadingMessage('Enviando documento...')
            resetRedSocketMessage()

            try {
                await analyzerApi.analyzeProject(
                    selectedFile,
                    selectedDocumentType?.apiId || selectedDocType
                )
                setLoadingMessage(
                    'Arquivo enviado. Aguardando confirmação do servidor...'
                )
            } catch (error) {
                console.error('Erro ao enviar arquivo para análise:', error)
                setLoadingMessage('Erro ao enviar arquivo. Tente novamente.')
                setIsLoading(false)
                setUploadPhase('idle')
                return
            }
        },
        [
            analyzerApi,
            resetRedSocketMessage,
            selectedDocType,
            selectedDocumentType,
            validateFileSize,
        ]
    )

    const handleCloseModal = () => {
        setModal(false)
        setDocumentToDelete(null)
    }

    const handleConfirmModal = () => {
        if (!documentToDelete) return

        api.deleteDocumentAnalysis(documentToDelete)
            .then(() => {
                getProjects()
                refreshAnalyses()
                console.log('Documento apagado com sucesso')
            })
            .catch((error) => {
                console.error('Erro ao apagar documento:', error)
            })
        setModal(false)
        setDocumentToDelete(null)
    }

    return (
        <Section>
            <Modal
                open={modal}
                onClose={handleCloseModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{fontWeight: 600, mb: 2}}
                    >
                        Confirmar exclusão
                    </Typography>
                    <Typography
                        id="modal-modal-description"
                        sx={{mb: 3, color: '#5E5E5E'}}
                    >
                        Tem certeza que deseja excluir esta análise de
                        documento? Esta ação não pode ser desfeita.
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleCloseModal}
                            sx={{textTransform: 'none'}}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmModal}
                            sx={{textTransform: 'none'}}
                        >
                            Excluir
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <div
                style={{
                    textAlign: 'left',
                    marginLeft: 0,
                    marginTop: 0,
                    width: '90%',
                    paddingTop: '40px',
                }}
            >
                <Title>Análise de Documentos</Title>
                <Typography
                    variant="h6"
                    color="textSecondary"
                    style={{
                        marginTop: '10px',
                        marginBottom: '20px',
                        fontSize: '16px',
                        textAlign: 'left',
                    }}
                >
                    Ferramenta inteligente para análise e processamento de
                    documentos
                </Typography>
            </div>
            <Body>
                <div
                    style={{
                        width: 'calc(100% - 40px)',
                        margin: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <label
                        style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: '#858585',
                            paddingBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        TIPO DO DOCUMENTO
                        <Tooltip
                            title='Selecione o tipo principal do documento enviado. Se você está enviando o edital com seus anexos consolidados em um arquivo único, selecione "Edital (com ou sem anexos)". A análise considerará automaticamente o conteúdo dos anexos integrados.'
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '400px',
                                        fontSize: '13px',
                                        padding: '12px 16px',
                                        backgroundColor: '#333',
                                        color: '#fff',
                                    },
                                },
                            }}
                        >
                            <InfoIcon
                                style={{color: '#DFDFDF'}}
                                fontSize="small"
                            />
                        </Tooltip>
                    </label>
                    <FormControl
                        fullWidth
                        variant="outlined"
                        sx={{height: '60px'}}
                    >
                        <Select
                            value={selectedDocType}
                            onChange={(e) =>
                                setSelectedDocType(e.target.value as string)
                            }
                            sx={{
                                height: '60px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {visibleDocumentTypes.map((item) => {
                                return (
                                    <MenuItem
                                        key={item.apiId}
                                        value={item.apiId}
                                        sx={{height: '60px'}}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: item.soft,
                                                    borderRadius: 8,
                                                    border: `1px solid ${item.accent}`,
                                                }}
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    style={{
                                                        color: item.accent,
                                                    }}
                                                >
                                                    <path d={item.iconPath} />
                                                </svg>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <span style={{fontWeight: 500}}>
                                                    {item.name}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: '#6b6b6b',
                                                    }}
                                                >
                                                    {item.desc}
                                                </span>
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '16px',
                        marginLeft: '20px',
                        marginRight: '20px',
                        padding: '10px 12px',
                        backgroundColor: '#f0f4ff',
                        borderRadius: '6px',
                        color: '#555',
                    }}
                >
                    <span
                        style={{
                            fontSize: '14px',
                            lineHeight: '1.5',
                            color: '#666',
                        }}
                    >
                        ⓘ A análise processa apenas um arquivo por vez. Para
                        obter o melhor resultado, consolide o edital e todos os
                        seus anexos (TR, minuta de contrato, ETP, planilhas) em
                        um único PDF antes do upload.
                    </span>
                </div>
                {showAlertFileSize && (
                    <Alert
                        severity="error"
                        onClose={() => {
                            setShowAlertFileSize(false)
                        }}
                        style={{marginBottom: '16px'}}
                    >
                        O tamanho máximo permitido por arquivo é 20 MB.
                    </Alert>
                )}
                {pdfAlertMessage && (
                    <Alert
                        severity="info"
                        onClose={() => {
                            setPdfAlertMessage('')
                        }}
                        style={{marginBottom: '16px'}}
                    >
                        {pdfAlertMessage}
                    </Alert>
                )}
                {loadingList ? (
                    <div style={{width: '100%'}}>
                        <Skeleton
                            variant="rounded"
                            height={200}
                            style={{
                                margin: '20px',
                                borderRadius: '16px',
                                backgroundColor: '#f0f0f0',
                            }}
                        />
                        {Array.from({length: 3}).map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rounded"
                                height={120}
                                style={{
                                    marginBottom: '16px',
                                    borderRadius: '16px',
                                    backgroundColor: '#f0f0f0',
                                }}
                            />
                        ))}
                    </div>
                ) : DocAnalysisList.length > 0 || isLoading ? (
                    <>
                        {isLoading ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                    marginTop: '20px',
                                    marginBottom: '20px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        padding: '16px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        backgroundColor: '#fafafa',
                                    }}
                                >
                                    <img
                                        src={
                                            progress >= 100
                                                ? aiIcon
                                                : getFileIcon(file?.name)
                                        }
                                        alt="Arquivo"
                                        style={{width: '63px', height: '63px'}}
                                    />
                                    <div
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            style={{fontWeight: 500}}
                                        >
                                            {file
                                                ? file.name
                                                : 'Carregando arquivo...'}
                                        </Typography>
                                        {progress < 100 ? (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    marginTop: '8px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {file
                                                            ? `${(
                                                                  (file.size /
                                                                      1024 /
                                                                      1024) *
                                                                  (progress /
                                                                      100)
                                                              ).toFixed(
                                                                  2
                                                              )} MB / ${(
                                                                  file.size /
                                                                  1024 /
                                                                  1024
                                                              ).toFixed(2)} MB`
                                                            : ''}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >{`${Math.round(
                                                        progress
                                                    )}%`}</Typography>
                                                </div>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progress}
                                                    sx={{
                                                        borderRadius: '27px',
                                                        height: '10px',
                                                        marginTop: '4px',
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <Typography
                                                component="span"
                                                style={{
                                                    marginTop: '8px',
                                                    color: '#5E5E5E',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                <CircularProgress
                                                    size={18}
                                                    thickness={5}
                                                    sx={{
                                                        color: '#5E5E5E',
                                                        marginRight: '8px',
                                                        verticalAlign: 'middle',
                                                    }}
                                                />
                                                {loadingMessage}
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                style={{
                                    width: 'calc(100% - 40px)',
                                    height: '200px',
                                    backgroundColor: selectedDocBg || '#f5f5f5',
                                    border: '2px dashed #ccc',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '20px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault()
                                    if (
                                        e.dataTransfer.files &&
                                        e.dataTransfer.files.length > 0
                                    ) {
                                        const file = e.dataTransfer.files[0]
                                        e.dataTransfer.clearData()

                                        if (!validateFileSize(file)) {
                                            return
                                        }

                                        handleAnalyzeProject(file)
                                    }
                                }}
                                onClick={() => {
                                    const input =
                                        document.createElement('input')
                                    input.type = 'file'
                                    input.accept = '.pdf,.docx'
                                    input.multiple = false
                                    input.onchange = (e) => {
                                        const files = (
                                            e.target as HTMLInputElement
                                        ).files
                                        if (files && files.length > 0) {
                                            const file = files[0]

                                            if (!validateFileSize(file)) {
                                                return
                                            }

                                            handleAnalyzeProject(file)
                                        }
                                    }
                                    input.click()
                                }}
                            >
                                <div
                                    style={{
                                        textAlign: 'center',
                                        color: '#292D32',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        style={{marginBottom: '8px'}}
                                    >
                                        Arraste e solte seu{' '}
                                        {selectedDocumentType?.name ??
                                            'arquivo'}{' '}
                                        aqui
                                    </Typography>
                                    <Typography color="#5E5E5E" variant="body2">
                                        Arquivos suportados: PDF e DOCX
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        style={{
                                            margin: '8px 0',
                                            backgroundColor: '#E1E1E1',
                                            color: '#848484',
                                        }}
                                    >
                                        Escolher arquivo
                                    </Button>
                                    <Typography color="#5E5E5E" variant="body2">
                                        Tamanho Máximo: 20MB
                                    </Typography>
                                </div>
                            </div>
                        )}
                        {DocAnalysisList.map((arquivo, arquivoIndex) => {
                            const items = analyzedItems[arquivoIndex]
                            const isLoadingDetails =
                                detailsLoading[arquivoIndex]
                            const documentTypeBadge =
                                visibleDocumentTypes.find(
                                    (item) =>
                                        item.apiId === arquivo.document_type_id
                                ) || null

                            return (
                                <Accordion
                                    key={arquivoIndex}
                                    expanded={expandedIndex === arquivoIndex}
                                    onChange={handleAccordionChange(
                                        arquivoIndex,
                                        arquivo.doc_analysis_uuid
                                    )}
                                    sx={{
                                        borderRadius: '16px',
                                        marginBottom: '16px',
                                        boxShadow: 'none',
                                        border: '1px solid rgba(0, 0, 0, 0.12)',
                                        '&:before': {
                                            display: 'none',
                                        },
                                        '&.MuiAccordion-root': {
                                            borderRadius: '16px',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`panel${arquivoIndex}-content`}
                                        id={`panel${arquivoIndex}-header`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                width: '100%',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <img
                                                src={getFileIcon(
                                                    arquivo.file_url
                                                )}
                                                alt="Arquivo"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                }}
                                            />
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    width: '100%',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={arquivo.file_url}
                                                    >
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: '8px',
                                                                '&:hover': {
                                                                    color: 'primary.main',
                                                                    cursor: 'pointer',
                                                                },
                                                            }}
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation()

                                                                const res =
                                                                    api.downloadAnalyzedItem(
                                                                        arquivo.doc_analysis_uuid,
                                                                        arquivo.file_url
                                                                    )
                                                                console.log(
                                                                    'Iniciando download de:',
                                                                    res
                                                                )
                                                            }}
                                                        >
                                                            {
                                                                arquivo.doc_analysis_name
                                                            }
                                                            {documentTypeBadge && (
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        display:
                                                                            'inline-flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: '6px',
                                                                        marginLeft:
                                                                            '8px',
                                                                        padding:
                                                                            '4px 10px',
                                                                        borderRadius:
                                                                            '999px',
                                                                        border: `1px solid ${documentTypeBadge.accent}`,
                                                                        backgroundColor:
                                                                            documentTypeBadge.soft,
                                                                        color: documentTypeBadge.accent,
                                                                        fontSize:
                                                                            '12px',
                                                                        fontWeight: 700,
                                                                        lineHeight: 1,
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    {
                                                                        documentTypeBadge.short
                                                                    }
                                                                </Box>
                                                            )}
                                                            <DownloadForOfflineOutlinedIcon fontSize="small" />
                                                        </Typography>
                                                    </Tooltip>
                                                    <Tooltip title={'Excluir'}>
                                                        <IconButton
                                                            sx={{
                                                                color: 'red',
                                                                marginLeft:
                                                                    '8px',
                                                            }}
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation()
                                                                setDocumentToDelete(
                                                                    arquivo.doc_analysis_uuid
                                                                )
                                                                setModal(true)
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                        title={
                                                            isLoadingDetails
                                                                ? 'Carregando dados da análise...'
                                                                : 'Baixar análise em PDF'
                                                        }
                                                    >
                                                        <IconButton
                                                            disabled={Boolean(
                                                                isLoadingDetails
                                                            )}
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                handleDownloadAnalysisPDF(
                                                                    event,
                                                                    arquivoIndex,
                                                                    arquivo.doc_analysis_uuid,
                                                                    arquivo.file_url
                                                                )
                                                            }}
                                                        >
                                                            <PrintIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            justifyContent:
                                                                'center',
                                                            alignItems:
                                                                'center',
                                                            marginLeft: 'auto',
                                                            marginRight: '16px',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius:
                                                                    '50%',
                                                                backgroundColor:
                                                                    '#2eb936',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                color: 'white',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {arquivo
                                                                .status_data?.[
                                                                '0'
                                                            ] || 0}
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius:
                                                                    '50%',
                                                                backgroundColor:
                                                                    '#ffc421',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                color: 'white',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {arquivo
                                                                .status_data?.[
                                                                '1'
                                                            ] || 0}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius:
                                                                    '50%',
                                                                backgroundColor:
                                                                    '#dc2c2c',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                color: 'white',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {arquivo
                                                                .status_data?.[
                                                                '2'
                                                            ] || 0}
                                                        </Box>
                                                    </Box>
                                                </div>

                                                <Typography
                                                    component="span"
                                                    style={{
                                                        color: '#5E5E5E',
                                                        fontWeight: 400,
                                                        fontSize: '14px',
                                                        lineHeight: '180%',
                                                        letterSpacing: '0px',
                                                    }}
                                                >
                                                    {arquivo.created_at
                                                        ? new Date(
                                                              arquivo.created_at
                                                          ).toLocaleString(
                                                              'pt-BR',
                                                              {
                                                                  day: '2-digit',
                                                                  month: '2-digit',
                                                                  year: 'numeric',
                                                                  hour: '2-digit',
                                                                  minute: '2-digit',
                                                              }
                                                          )
                                                        : '-'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {isLoadingDetails && (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{padding: '8px 0'}}
                                            >
                                                Carregando detalhes...
                                            </Typography>
                                        )}

                                        {!isLoadingDetails &&
                                            items &&
                                            items.length > 0 && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '12px',
                                                    }}
                                                >
                                                    {items.map((item) => {
                                                        const {
                                                            icon,
                                                            label,
                                                            color,
                                                        } = getStatusVisuals(
                                                            item.status
                                                        )

                                                        return (
                                                            <div
                                                                key={
                                                                    item.analyzed_item_uuid
                                                                }
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    gap: '12px',
                                                                    padding:
                                                                        '12px',
                                                                    border: '1px solid #e0e0e0',
                                                                    borderRadius:
                                                                        '12px',
                                                                    backgroundColor:
                                                                        '#fafafa',
                                                                }}
                                                            >
                                                                <img
                                                                    src={icon}
                                                                    alt={label}
                                                                    style={{
                                                                        width: '32px',
                                                                        height: '32px',
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        flex: 1,
                                                                        display:
                                                                            'flex',
                                                                        flexDirection:
                                                                            'column',
                                                                        gap: '4px',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            color,
                                                                        }}
                                                                    >
                                                                        {item.analyzed_item_title ||
                                                                            label}
                                                                    </Typography>
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontSize:
                                                                                '18px',
                                                                            color: '#5E5E5E',
                                                                        }}
                                                                    >
                                                                        {
                                                                            item.content
                                                                        }
                                                                    </Typography>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}

                                        {!isLoadingDetails &&
                                            (!items || items.length === 0) && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{padding: '8px 0'}}
                                                >
                                                    Documento em processo de
                                                    análise.
                                                </Typography>
                                            )}
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}
                        <Pagination
                            count={totalPages}
                            variant="outlined"
                            shape="rounded"
                            page={page}
                            onChange={(_event, value: number) => {
                                setPage(value)
                                setAnalyzedItems([])
                                setExpandedIndex(null)
                            }}
                        />
                    </>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',

                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        <div
                            style={{
                                width: 'calc(100% - 40px)',
                                height: '200px',
                                backgroundColor: selectedDocBg || '#f5f5f5',
                                border: '2px dashed #ccc',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '20px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault()
                                if (
                                    e.dataTransfer.files &&
                                    e.dataTransfer.files.length > 0
                                ) {
                                    const file = e.dataTransfer.files[0]
                                    e.dataTransfer.clearData()

                                    if (!validateFileSize(file)) {
                                        return
                                    }

                                    handleAnalyzeProject(file)
                                }
                            }}
                            onClick={() => {
                                const input = document.createElement('input')
                                input.type = 'file'
                                input.accept = '.pdf,.docx'
                                input.multiple = false
                                input.onchange = (e) => {
                                    const files = (e.target as HTMLInputElement)
                                        .files
                                    if (files && files.length > 0) {
                                        const file = files[0]

                                        if (!validateFileSize(file)) {
                                            return
                                        }

                                        handleAnalyzeProject(file)
                                    }
                                }
                                input.click()
                            }}
                        >
                            <div
                                style={{
                                    textAlign: 'center',
                                    color: '#292D32',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    style={{marginBottom: '8px'}}
                                >
                                    Arraste e solte seu{' '}
                                    {selectedDocumentType?.name ?? 'arquivo'}{' '}
                                    aqui
                                </Typography>
                                <Typography color="#5E5E5E" variant="body2">
                                    Arquivos suportados: PDF e DOCX
                                </Typography>
                                <Button
                                    variant="contained"
                                    style={{
                                        margin: '8px 0',
                                        backgroundColor: '#E1E1E1',
                                        color: '#848484',
                                    }}
                                >
                                    Escolher arquivo
                                </Button>
                                <Typography color="#5E5E5E" variant="body2">
                                    Tamanho Máximo: 20MB
                                </Typography>
                            </div>
                        </div>
                        <Typography
                            variant="h6"
                            style={{
                                marginBottom: '20px',
                                color: 'rgb(102, 102, 102)',
                            }}
                        >
                            Nenhum documento analisado ainda.
                        </Typography>
                        <img
                            src={empty}
                            alt="Nenhum edital"
                            style={{width: '507px', marginBottom: '20px'}}
                        />
                    </div>
                )}
            </Body>
            <Typography
                style={{
                    width: '50%',
                }}
                variant="body2"
                sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#5E5E5E',
                }}
            >
                Esta análise é gerada por inteligência artificial e pode conter
                alertas imprecisos (falsos positivos) ou deixar de identificar
                problemas existentes. Os resultados não substituem a revisão por
                profissional qualificado.
            </Typography>

            <Snackbar
                slotProps={{
                    content: {
                        sx: {
                            backgroundColor: colors.primariaEscura,
                        },
                    },
                }}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                open={Toast.open}
                onClose={handleCloseToast}
                message="Nova funcionalidade em fase de testes. Uso liberado por tempo limitado, sem consumo de créditos. Seu feedback é essencial para nós!"
                key={'topcenter'}
                action={
                    <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={handleCloseToast}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            />
        </Section>
    )
}
